import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Leaf, ArrowUp, Mail, ListTree } from "lucide-react";
import { CONTACT_EMAIL, LOGO_URL } from "@/termsData";

const Bullet = ({ item }) => (
  <li className="flex gap-3 leading-relaxed text-[15px] text-[var(--ld-text)]">
    <span className="mt-[9px] h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--ld-lime)]" />
    <span>
      {item.label && (
        <span className="font-extrabold text-[var(--ld-lime-bright)]">
          {item.label}:{" "}
        </span>
      )}
      {item.text}
    </span>
  </li>
);

const Section = ({ section, index }) => (
  <section
    id={section.id}
    data-testid={`section-${section.id}`}
    className="scroll-mt-28 gc-rise"
    style={{ animationDelay: `${0.04 * index + 0.15}s` }}
  >
    <h2 className="mb-3 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--ld-lime)]">
      {section.title}
    </h2>
    {section.lead && (
      <p className="mb-3 text-[15px] leading-relaxed text-[var(--ld-text)]">
        {section.lead}
      </p>
    )}
    {section.paragraph && (
      <p className="text-[15px] leading-relaxed text-[var(--ld-text)]">
        {section.paragraph}
      </p>
    )}
    {section.items && (
      <ul className="space-y-3">
        {section.items.map((item, i) => (
          <Bullet key={i} item={item} />
        ))}
      </ul>
    )}
  </section>
);

export default function PolicyDoc({
  docTitle,
  pageTitle,
  effectiveDate,
  intro,
  sections,
  contactNumber,
}) {
  const navigate = useNavigate();
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    document.title = pageTitle;
    window.scrollTo(0, 0);
    const onScroll = () => setShowTop(window.scrollY > 480);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [pageTitle]);

  const tocSections = [
    ...sections,
    { id: "contact", title: `${contactNumber}. Contact Us` },
  ];

  return (
    <div className="ld-root ld-grain relative min-h-screen">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 border-b border-[var(--ld-line)] bg-[var(--ld-bg)]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4 sm:px-6">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="grid h-10 w-10 place-items-center rounded-full bg-[var(--ld-surface)] text-[var(--ld-text)] transition-colors duration-200 hover:bg-[var(--ld-surface-2)]"
            aria-label="Back to home"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-black leading-tight tracking-wide text-[var(--ld-text)] sm:text-2xl">
              {docTitle}
            </h1>
            <p className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--ld-lime)]">
              <Leaf className="h-3.5 w-3.5" /> GrowCalc
            </p>
          </div>
          <img
            src={LOGO_URL}
            alt="GrowCalc logo"
            className="h-10 w-10 rounded-xl object-contain"
          />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-4 pb-24 pt-8 sm:px-6 lg:grid lg:grid-cols-[230px_1fr] lg:gap-10">
        {/* Table of contents (desktop) */}
        <aside className="hidden lg:block">
          <nav
            data-testid="toc"
            className="sticky top-28 rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/70 p-5 backdrop-blur-xl"
          >
            <p className="mb-3 flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-[var(--ld-lime)]">
              <ListTree className="h-4 w-4" /> Contents
            </p>
            <ul className="space-y-1.5">
              {tocSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`toc-link-${s.id}`}
                    className="block rounded-lg px-2 py-1 text-[13.5px] font-semibold text-[var(--ld-muted)] transition-colors duration-200 hover:bg-[var(--ld-lime)]/10 hover:text-[var(--ld-lime-bright)]"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Document body */}
        <article className="min-w-0">
          <div className="gc-rise rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/70 p-6 backdrop-blur-xl sm:p-9">
            <p
              data-testid="effective-date"
              className="mb-4 inline-block rounded-full bg-[var(--ld-lime)]/15 px-4 py-1.5 text-sm font-extrabold text-[var(--ld-lime-bright)]"
            >
              Effective date: {effectiveDate}
            </p>
            <p className="text-[15px] leading-relaxed text-[var(--ld-text)]">
              {intro}
            </p>
          </div>

          <div className="mt-8 space-y-9">
            {sections.map((section, i) => (
              <Section key={section.id} section={section} index={i} />
            ))}

            {/* Contact section */}
            <section
              id="contact"
              data-testid="section-contact"
              className="scroll-mt-28 gc-rise"
              style={{ animationDelay: `${0.04 * sections.length + 0.15}s` }}
            >
              <h2 className="mb-3 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--ld-lime)]">
                {contactNumber}. Contact Us
              </h2>
              <div className="rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/70 p-5 backdrop-blur-xl sm:p-6">
                <p className="flex flex-wrap items-center gap-1.5 text-[15px] leading-relaxed text-[var(--ld-text)]">
                  <Mail className="h-4 w-4 shrink-0 text-[var(--ld-lime)]" />
                  Questions? Contact us at{" "}
                  <a
                    data-testid="contact-email"
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-extrabold text-[var(--ld-lime-bright)] underline decoration-[var(--ld-lime)]/40 underline-offset-2 transition-colors hover:text-[var(--ld-lime)]"
                  >
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              </div>
            </section>
          </div>

          {/* Footer */}
          <footer className="mt-12 flex items-center gap-2 border-t border-[var(--ld-line)] pt-6 text-sm font-semibold text-[var(--ld-muted)]">
            <Leaf className="h-4 w-4 text-[var(--ld-lime)]" />
            <span data-testid="footer-copyright">
              &copy; 2026 GrowCalc. All rights reserved.
            </span>
          </footer>
        </article>
      </main>

      {/* Back to top */}
      <button
        data-testid="back-to-top"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 right-6 z-50 grid h-12 w-12 place-items-center rounded-full bg-[var(--ld-lime)] text-[var(--ld-bg)] shadow-lg transition-all duration-300 hover:bg-[var(--ld-lime-bright)] ${
          showTop
            ? "translate-y-0 opacity-100"
            : "pointer-events-none translate-y-4 opacity-0"
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  );
}
